import type { ValidationSchema } from '@wix/editor-elements-corvid-utils';
import { composeSDKFactories } from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import { createLeanComponentSDK } from '@wix/lean-components-infra/runtime/sdk';
import type { CorvidTypes } from '@wix/editor-elements-types/corvid';
import type {
  IRichContentViewerProps,
  FileUploadPluginFile,
} from '../RichContentViewer.types';

const sdkSchema = {
  content: {
    type: 'object',
  },
};

const validationSchema: ValidationSchema = {
  type: ['object'],
  properties: {
    content: {
      type: ['object'],
    },
  },
};
export type RichContentOwnSdk = {
  content?: any;
  value?: any;
  onNodeClick: (handler: (event: { node: any }) => void) => void;
  plugins?: {
    file?: {
      resolveFileUrl: (file: FileUploadPluginFile) => Promise<string>;
    };
    hashtag?:
      | {
          enabled: true;
          /**
           * A template for the href attribute of the anchor tag that wraps the hashtag.
           * The template should include a placeholder `{{hashtag}}` for the hashtag text.
           * For example, if the template is '/search?query=%23{{hashtag}}',
           * the href attribute of the anchor tag will be '/search?query=%23hashtagText'.
           */
          hrefTemplate: string;
        }
      | { enabled: false };
  };
};

const mixins = null;

const leanSdk = createLeanComponentSDK<IRichContentViewerProps>({
  sdkSchema,
  validationSchema,
  mixins,
});

const richContentViewerSdkFactory: CorvidTypes.CorvidSDKFactory<
  IRichContentViewerProps,
  RichContentOwnSdk
> = ({ setProps, props, registerEvent }) => {
  const fileResolutions: Record<string, string> = {};

  return {
    get value() {
      return props.content;
    },
    set value(value) {
      setProps({ content: value });
    },
    set plugins(value: RichContentOwnSdk['plugins']) {
      const resolveFileUrl = value?.file?.resolveFileUrl;

      if (resolveFileUrl) {
        registerEvent<FileUploadPluginFile>(
          'onResolveFileUrlRequest',
          async file => {
            fileResolutions[file.id] = await resolveFileUrl(file);

            setProps({ fileResolutions: { ...fileResolutions } });
          },
        );
      }

      if (value?.hashtag?.enabled) {
        const hashtagHrefTemplate = value.hashtag.hrefTemplate;
        setProps({ hashtagHrefTemplate });
      }
    },
    onNodeClick: (handler: any) => {
      return registerEvent('onNodeClick', handler);
    },
  };
};

export const sdk = composeSDKFactories<IRichContentViewerProps, any>([
  leanSdk,
  richContentViewerSdkFactory,
]);

export default createComponentSDKModel(sdk);
